// EntitlementModal view configuration — Cluster C.3.
//
// Centralized strings, icons, and accent colors per view. Keeping copy
// here means product can tune messaging in one place without touching
// JSX. Each view's `accent` drives the icon-circle background + the
// primary CTA color.
//
// Brand palette per Cluster A.4-clean:
//   brand blue  #4A90E2  — primary actions, starter
//   navy        #1A365D  — wordmark, premium tier accent
//   success     #16A34A  — confirmations
//   warning     #D97706  — past-due, attention
//   error       #DC2626  — failures
//
// Each view exposes:
//   icon            — lucide-react component
//   accent          — hex color (icon bg tint + CTA bg)
//   eyebrow         — small uppercase label above headline
//   headline        — h2-level main message
//   helperBody(d)   — function that returns descriptive paragraph given
//                     the decision dict; allows interpolation of plan
//                     names, periods, etc.
//
// CTAs are defined inline in the modal because each one needs different
// handlers (navigation, dismiss, view transition, etc.); only the
// labels are pulled from here for translation-readiness.
import {
  Sparkles,
  AlertTriangle,
  MapPin,
  Wrench,
  ArrowUpCircle,
  Layers,
  ShoppingBag,
  Loader2,
  AlertOctagon,
} from 'lucide-react';

// Pretty plan-name helper. plan_key is lowercase canonical; users see
// title-case. Centralised so all views display consistently.
export function prettyPlan(planKey) {
  if (!planKey) return '';
  const map = { free: 'Free', starter: 'Starter', core: 'Core', premium: 'Premium' };
  return map[planKey] || planKey[0].toUpperCase() + planKey.slice(1);
}

// Pretty period helper — "per month" / "every quarter" / etc.
export function prettyPeriod(period) {
  return ({
    month: 'every month',
    quarter: 'every quarter',
    half_year: 'every six months',
    year: 'every year',
    unlimited: '',
  })[period] || '';
}

// View metadata. Functions over the decision (`d`) build readable copy
// per Q9.2 + Q5.4 lock semantics.
export const VIEW_CONFIG = {
  not_subscribed: {
    icon: Sparkles,
    accent: '#4A90E2',
    eyebrow: 'Subscribe to unlock',
    headline: (d, meta) =>
      meta?.display_name
        ? `Start a subscription to book ${meta.display_name}`
        : 'Start a subscription to book this service',
    helperBody: (d) =>
      d?.upgrade_to_tier
        ? `${prettyPlan(d.upgrade_to_tier)} includes this service in its monthly allowance, plus all the AI care features you'd expect.`
        : `nsofar's paid tiers include this service plus everything else you need to coordinate care for your parents.`,
    primaryLabel: (d) =>
      d?.upgrade_to_tier ? `See ${prettyPlan(d.upgrade_to_tier)} plan` : 'View plans',
    secondaryLabel: 'Maybe later',
  },

  past_due: {
    icon: AlertOctagon,
    accent: '#D97706',
    eyebrow: 'Payment issue',
    headline: () => 'Your subscription needs attention',
    helperBody: () =>
      "We couldn't charge your card on the last billing cycle. Once you update your payment method, bookings resume automatically.",
    primaryLabel: () => 'Manage subscription',
    secondaryLabel: 'Close',
  },

  pincode_unserved: {
    icon: MapPin,
    accent: '#0EA5E9',
    eyebrow: 'Coverage',
    headline: (d, meta) =>
      meta?.display_name
        ? `${meta.display_name} isn't available at your pincode yet`
        : "That service isn't available at your pincode yet",
    helperBody: () =>
      "We're expanding partner coverage region by region. Your interest is logged — we'll notify you as soon as it's available in your area.",
    primaryLabel: () => 'Got it',
    secondaryLabel: null,
  },

  service_inactive: {
    icon: Wrench,
    accent: 'var(--text-light)',
    eyebrow: 'Temporarily paused',
    headline: () => 'This service is temporarily unavailable',
    helperBody: () =>
      "We've paused this service while we coordinate with our partner. Please check back shortly — most pauses resolve within 24 hours.",
    primaryLabel: () => 'Close',
    secondaryLabel: null,
  },

  upgrade_required: {
    icon: ArrowUpCircle,
    accent: '#1A365D',
    eyebrow: 'Higher tier needed',
    headline: (d, meta) =>
      meta?.display_name
        ? `${meta.display_name} is on a higher tier`
        : 'This service is on a higher tier',
    helperBody: (d) =>
      d?.upgrade_to_tier
        ? `${prettyPlan(d.upgrade_to_tier)} unlocks this service plus a wider range of specialist care at home.`
        : "Your current plan doesn't include this service. Upgrade to access it.",
    primaryLabel: (d) =>
      d?.upgrade_to_tier ? `Upgrade to ${prettyPlan(d.upgrade_to_tier)}` : 'See plans',
    secondaryLabel: 'Close',
  },

  // Q5.4 View 1 — soft + hard cap share this view, copy adapts via decision.
  cap_upgrade: {
    icon: Layers,
    accent: '#4A90E2',
    eyebrow: (d) =>
      d?.outcome === 'hard_cap' ? "You've used this period's allowance" : "You're at your monthly allowance",
    headline: (d, meta) => {
      const name = meta?.display_name || 'this service';
      return `Get more ${name} ${d?.outcome === 'hard_cap' ? 'this period' : 'this month'}`;
    },
    helperBody: (d) => {
      const target = prettyPlan(d?.upgrade_to_tier);
      const delta = d?.upgrade_inclusions_delta;
      if (target && delta && delta > 0) {
        return `${target} adds ${delta} more per month at no extra cost beyond the monthly upgrade. Or buy this one-off below.`;
      }
      if (target) {
        return `${target} includes more of this service every month. Or grab this one-off below.`;
      }
      return d?.alacarte_available
        ? "You can book this one-off as à la carte — your bundle allowance refreshes next period."
        : "Your allowance refreshes next period.";
    },
    primaryLabel: (d) =>
      d?.upgrade_to_tier ? `Upgrade to ${prettyPlan(d.upgrade_to_tier)}` : null,
    secondaryLabel: 'See à la carte price',
    tertiaryLabel: 'Not now',
  },

  // Q5.4 View 2 — also used Premium-direct + alacarte-only services.
  cap_alacarte: {
    icon: ShoppingBag,
    accent: '#16A34A',
    eyebrow: 'Pay once',
    headline: (d, meta) =>
      meta?.display_name
        ? `Book ${meta.display_name} once`
        : 'Book this service once',
    helperBody: (d, _meta, quote) => {
      if (quote?.is_fee_only && quote.fee_label) {
        return `${quote.fee_label} is shown below. Medicine cost is billed at MRP at delivery time — no markup.`;
      }
      if (d?.outcome === 'hard_cap') {
        return "This is a one-off purchase. It doesn't refill your monthly allowance — you'll still get the full bundle quota when the period resets.";
      }
      return "A one-off purchase. Your monthly allowance keeps its remaining quota and resets normally.";
    },
    primaryLabel: () => 'Continue to payment',
    secondaryLabel: 'Cancel',
  },

  alacarte_loading: {
    icon: Loader2,
    accent: '#4A90E2',
    eyebrow: 'Getting the price',
    headline: () => 'Just a moment...',
    helperBody: () => 'Locking in the latest price from our partner.',
    primaryLabel: () => null,
    secondaryLabel: null,
  },

  error: {
    icon: AlertTriangle,
    accent: '#DC2626',
    eyebrow: 'Something went wrong',
    headline: () => "Couldn't check eligibility",
    helperBody: (_d, _meta, _q, errorMessage) =>
      errorMessage || 'Please try again in a moment.',
    primaryLabel: () => 'Try again',
    secondaryLabel: 'Close',
  },
};

// Helper: coerce a view's headline/eyebrow/etc. field — which may be a
// string OR a function — into a resolved string. Functions receive
// (decision, serviceMeta, quote, errorMessage).
export function resolve(field, decision, serviceMeta, quote, errorMessage) {
  if (typeof field === 'function') {
    return field(decision, serviceMeta, quote, errorMessage);
  }
  return field;
}
